import { useTranslation } from 'react-i18next';
import React, { useState, useCallback, useEffect } from 'react';
import { RoutingControls, DEFAULT_ROUTING, type RoutingChoice } from '../Info/RoutingControls';
import {
  Target, Play, Footprints, Car, Bike, Circle, Loader2, MapPin, AlertTriangle, X,
} from 'lucide-react';
import { DraggablePanel } from '../common/DraggablePanel';
import { getMapColors } from '../../themes/mapTheme';
import { cn } from '../../utils/cn';
import type {
  CatchmentSelection, CatchmentTimes, CatchmentOptions,
  LayerControl, TrafficControl,
} from '../Info/FeatureInfoPanel';

/* ── Props ── */
interface CannibalizationPanelProps {
  onClose: () => void;
  point1: [number, number] | null;   // [lon, lat]
  point2: [number, number] | null;   // [lon, lat]
  selectingPoint: 1 | 2;
  onSetSelecting: (which: 1 | 2) => void;
  onPointChange: (which: 1 | 2, lonLat: [number, number] | null) => void;
  onStartCannibalization: (
    selections: CatchmentSelection[],
    times: CatchmentTimes,
    options: CatchmentOptions,
    analysisName: string,
  ) => Promise<void>;
  isLoading?: boolean;
  error?: string | null;
  onDismissError?: () => void;
}

/* Legacy defaults */
const DEFAULT_WALKIN = ['5', '10', '15', '', ''];
const DEFAULT_DRIVE  = ['5', '15', '30', '45', ''];
const DEFAULT_MOTOR  = ['5', '15', '30', '45', ''];
const DEFAULT_RING   = ['0.25', '0.75', '1', '', ''];

/* ── Reusable UI bits ── */

const Pill: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={cn(
      'px-2 py-0.5 rounded text-[10px] font-medium transition-colors border',
      active
        ? 'bg-gn-blue-light border-gn-blue text-gn-blue'
        : 'bg-gn-secondary border-gn-light text-gn-secondary hover:text-gn-primary hover:border-gn',
    )}
  >
    {children}
  </button>
);

const TimeRow: React.FC<{
  icon: React.ReactNode; label: string; values: string[];
  onChange: (v: string[]) => void; unit: string;
}> = ({ icon, label, values, onChange, unit }) => (
  <div className="flex items-center gap-1.5">
    {icon}
    <span className="text-[10px] text-gn-primary w-10 shrink-0">{label}</span>
    {values.map((t, i) => (
      <input key={i} type="text" value={t}
        onChange={(e) => { const n = [...values]; n[i] = e.target.value; onChange(n); }}
        className="w-11 px-1 py-1 text-[11px] text-center text-gn-primary bg-gn-tertiary border border-gn rounded focus:border-gn-blue focus:outline-none"
        placeholder="0" />
    ))}
    <span className="text-[9px] text-gn-secondary">{unit}</span>
  </div>
);

/* ══════════════════════════════════════════════════════════════
   Component
   ══════════════════════════════════════════════════════════════ */

export const CannibalizationPanel: React.FC<CannibalizationPanelProps> = ({
  onClose,
  point1,
  point2,
  selectingPoint,
  onSetSelecting,
  onPointChange,
  onStartCannibalization,
  isLoading = false,
  error = null,
  onDismissError,
}) => {
  const { t } = useTranslation();
  /* Coordinate inputs for manual entry */
  const [lat1, setLat1] = useState('');
  const [lon1, setLon1] = useState('');
  const [lat2, setLat2] = useState('');
  const [lon2, setLon2] = useState('');

  /* Analysis name — required */
  const [analysisName, setAnalysisName] = useState('');

  /* Travel mode toggles */
  const [walkEnabled, setWalkEnabled] = useState(true);
  const [driveEnabled, setDriveEnabled] = useState(true);
  const [motorEnabled, setMotorEnabled] = useState(false);
  const [ringEnabled, setRingEnabled] = useState(false);

  /* Time inputs */
  const [walkinTimes, setWalkinTimes] = useState(DEFAULT_WALKIN);
  const [driveTimes, setDriveTimes]   = useState(DEFAULT_DRIVE);
  const [motorTimes, setMotorTimes]   = useState(DEFAULT_MOTOR);
  const [ringDists, setRingDists]     = useState(DEFAULT_RING);

  /* Layer & traffic control */
  const [layerControl, setLayerControl]     = useState<LayerControl>('nbHood');
  const [trafficControl, setTrafficControl] = useState<TrafficControl>('hvytrf');
  const [routing, setRouting] = useState<RoutingChoice>(DEFAULT_ROUTING);

  // Sync inputs when map points are selected
  useEffect(() => {
    if (point1) {
      setLon1(point1[0].toFixed(6));
      setLat1(point1[1].toFixed(6));
    }
  }, [point1]);

  useEffect(() => {
    if (point2) {
      setLon2(point2[0].toFixed(6));
      setLat2(point2[1].toFixed(6));
    }
  }, [point2]);

  /* Notify parent when manual coordinates change (on blur) */
  const syncPoint = useCallback((which: 1 | 2) => {
    const la = which === 1 ? lat1 : lat2;
    const lo = which === 1 ? lon1 : lon2;
    if (la && lo && !isNaN(parseFloat(la)) && !isNaN(parseFloat(lo))) {
      onPointChange(which, [parseFloat(lo), parseFloat(la)]);
    }
  }, [lat1, lon1, lat2, lon2, onPointChange]);

  const hasPoint1 = lat1 && lon1 && !isNaN(parseFloat(lat1)) && !isNaN(parseFloat(lon1));
  const hasPoint2 = lat2 && lon2 && !isNaN(parseFloat(lat2)) && !isNaN(parseFloat(lon2));
  const hasName = analysisName.trim().length > 0;
  const canStart = hasPoint1 && hasPoint2 && hasName && !isLoading;

  const handleStart = useCallback(() => {
    if (!canStart) return;

    const selections: CatchmentSelection[] = [
      {
        name: `${analysisName.trim()} - Point 1`,
        lat: parseFloat(lat1),
        lon: parseFloat(lon1),
        walkin: walkEnabled,
        drive: driveEnabled,
        motor: motorEnabled,
        ringBuffer: ringEnabled,
      },
      {
        name: `${analysisName.trim()} - Point 2`,
        lat: parseFloat(lat2),
        lon: parseFloat(lon2),
        walkin: walkEnabled,
        drive: driveEnabled,
        motor: motorEnabled,
        ringBuffer: ringEnabled,
      },
    ];

    const times: CatchmentTimes = {
      walkin: walkinTimes.map(Number),
      drive:  driveTimes.map(Number),
      motor:  motorTimes.map(Number),
      ring:   ringDists.map(Number),
    };
    const options: CatchmentOptions = { layerControl, trafficControl, routing };

    onStartCannibalization(selections, times, options, analysisName.trim());
  }, [canStart, analysisName, lat1, lon1, lat2, lon2, walkEnabled, driveEnabled, motorEnabled, ringEnabled,
      walkinTimes, driveTimes, motorTimes, ringDists, layerControl, trafficControl, routing, onStartCannibalization]);

  /* ── Point selection box ── */
  const PointBox: React.FC<{
    num: 1 | 2;
    lat: string; lon: string;
    onLatChange: (v: string) => void;
    onLonChange: (v: string) => void;
    filled: boolean;
  }> = ({ num, lat, lon, onLatChange, onLonChange, filled }) => {
    const isSelecting = selectingPoint === num;
    return (
      <div
        className={cn(
          'rounded border p-2 space-y-1 transition-colors cursor-pointer',
          isSelecting
            ? 'border-gn-blue bg-gn-blue-light'
            : filled
              ? 'border-gn-success bg-gn-success-light'
              : 'border-gn-light bg-gn-tertiary',
        )}
        onClick={() => onSetSelecting(num)}
      >
        <div className="flex items-center gap-1.5">
          <MapPin size={12} className={cn(
            isSelecting ? 'text-gn-blue' : filled ? 'text-gn-success' : 'text-gn-secondary',
          )} />
          <span className={cn(
            'text-[10px] font-semibold',
            isSelecting ? 'text-gn-blue' : filled ? 'text-gn-success' : 'text-gn-secondary',
          )}>
            {t('cannib.point', { num })}
          </span>
          {isSelecting && (
            <span className="text-[9px] text-gn-blue bg-gn-blue-light px-1.5 py-0.5 rounded animate-pulse">
              {t('cannib.click_map')}
            </span>
          )}
          {!isSelecting && filled && (
            <span className="text-[9px] text-gn-success">✓</span>
          )}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[9px] text-gn-secondary">{t('map.latitude')}</label>
            <input type="text" value={lat}
              onChange={(e) => onLatChange(e.target.value)}
              onBlur={() => syncPoint(num)}
              onKeyDown={(e) => { if (e.key === 'Enter') syncPoint(num); }}
              className="w-full px-2 py-1 text-[11px] text-gn-primary bg-gn-tertiary border border-gn rounded focus:border-gn-blue focus:outline-none"
              placeholder={t('analysis.lat_example')} />
          </div>
          <div>
            <label className="text-[9px] text-gn-secondary">{t('map.longitude')}</label>
            <input type="text" value={lon}
              onChange={(e) => onLonChange(e.target.value)}
              onBlur={() => syncPoint(num)}
              onKeyDown={(e) => { if (e.key === 'Enter') syncPoint(num); }}
              className="w-full px-2 py-1 text-[11px] text-gn-primary bg-gn-tertiary border border-gn rounded focus:border-gn-blue focus:outline-none"
              placeholder={t('analysis.lon_example')} />
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
    {/* ═══ Loading overlay (blocks entire screen) ═══ */}
    {isLoading && (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="flex flex-col items-center gap-4 bg-gn-primary border border-gn rounded-xl px-8 py-6 shadow-2xl">
          <Loader2 size={36} className="text-gn-blue animate-spin" />
          <div className="text-center">
            <p className="text-gn-primary font-medium text-sm">{t('analysis.started', { title: t('cannib.title') })}</p>
            <p className="text-gn-secondary text-[11px] mt-1">{t('cannib.analyzing')}</p>
          </div>
        </div>
      </div>
    )}

    {/* ═══ Error overlay ═══ */}
    {error && !isLoading && (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="flex flex-col items-center gap-4 bg-gn-primary border border-gn-danger rounded-xl px-8 py-6 shadow-2xl max-w-md">
          <AlertTriangle size={36} className="text-gn-danger" />
          <div className="text-center">
            <p className="text-gn-primary font-medium text-sm">{t('analysis.failed')}</p>
            <p className="text-gn-danger text-[11px] mt-1">{error}</p>
          </div>
          <button
            onClick={onDismissError}
            className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded bg-gn-tertiary text-gn-primary hover:bg-gn-hover transition-colors"
          >
            <X size={12} />
            {t('messages.close')}
          </button>
        </div>
      </div>
    )}

    <DraggablePanel title={t('cannib.title')} onClose={onClose} icon={<Target size={14} />} width={380}>
      <div className="p-3 space-y-3">
        {/* Analysis Name — required */}
        <div className="space-y-1">
          <span className="text-[10px] text-gn-secondary font-medium">
            {t('analysis.name')} <span className="text-gn-danger">*</span>
          </span>
          <input
            type="text"
            value={analysisName}
            onChange={(e) => setAnalysisName(e.target.value)}
            className={cn(
              'w-full px-2 py-1.5 text-[11px] text-gn-primary bg-gn-tertiary border rounded focus:outline-none',
              !hasName && analysisName !== undefined
                ? 'border-gn focus:border-gn-blue'
                : hasName
                  ? 'border-gn-success focus:border-gn-accent'
                  : 'border-gn focus:border-gn-blue',
            )}
            placeholder={t('analysis.name_placeholder')}
          />
        </div>

        {/* Point Selection */}
        <div className="space-y-2">
          <PointBox num={1} lat={lat1} lon={lon1}
            onLatChange={setLat1} onLonChange={setLon1} filled={!!hasPoint1} />
          <PointBox num={2} lat={lat2} lon={lon2}
            onLatChange={setLat2} onLonChange={setLon2} filled={!!hasPoint2} />
        </div>

        {/* Layer control */}
        <div className="space-y-1">
          <span className="text-[10px] text-gn-secondary font-medium">{t('analysis.layers')}</span>
          <div className="flex gap-1 flex-wrap">
            <Pill active={layerControl === 'nbHood'} onClick={() => setLayerControl('nbHood')}>{t('analysis.layer_nbhood')}</Pill>
            <Pill active={layerControl === 'allVisible'} onClick={() => setLayerControl('allVisible')}>{t('analysis.layer_all')}</Pill>
            <Pill active={layerControl === 'cosmos'} onClick={() => setLayerControl('cosmos')}>{t('analysis.layer_cosmos')}</Pill>
          </div>
        </div>

        {/* Traffic, direction and border */}
        <div className="space-y-1">
          <RoutingControls traffic={trafficControl} onTraffic={setTrafficControl} routing={routing} onRouting={setRouting} showTraffic={driveEnabled} />
        </div>

        {/* Travel modes + times */}
        <div className="space-y-2">
          <span className="text-[10px] text-gn-secondary font-medium">{t('analysis.modes')}</span>

          <div className="space-y-1">
            <label className="flex items-center gap-2 text-[11px] text-gn-primary">
              <input type="checkbox" checked={walkEnabled} onChange={(e) => setWalkEnabled(e.target.checked)}
                className="rounded accent-blue-500" />
              <Footprints size={12} style={{ color: getMapColors().walk[0] }} /> {t('analysis.walking')}
            </label>
            {walkEnabled && (
              <TimeRow icon={<span />} label="" values={walkinTimes} onChange={setWalkinTimes} unit={t('analysis.unit_min')} />
            )}
          </div>

          <div className="space-y-1">
            <label className="flex items-center gap-2 text-[11px] text-gn-primary">
              <input type="checkbox" checked={driveEnabled} onChange={(e) => setDriveEnabled(e.target.checked)}
                className="rounded accent-blue-500" />
              <Car size={12} style={{ color: getMapColors().drive[0] }} /> {t('analysis.driving')}
            </label>
            {driveEnabled && (
              <TimeRow icon={<span />} label="" values={driveTimes} onChange={setDriveTimes} unit={t('analysis.unit_min')} />
            )}
          </div>

          <div className="space-y-1">
            <label className="flex items-center gap-2 text-[11px] text-gn-primary">
              <input type="checkbox" checked={motorEnabled} onChange={(e) => setMotorEnabled(e.target.checked)}
                className="rounded accent-blue-500" />
              <Bike size={12} style={{ color: getMapColors().motor[0] }} /> {t('analysis.motorcycle')}
            </label>
            {motorEnabled && (
              <TimeRow icon={<span />} label="" values={motorTimes} onChange={setMotorTimes} unit={t('analysis.unit_min')} />
            )}
          </div>

          <div className="space-y-1">
            <label className="flex items-center gap-2 text-[11px] text-gn-primary">
              <input type="checkbox" checked={ringEnabled} onChange={(e) => setRingEnabled(e.target.checked)}
                className="rounded accent-blue-500" />
              <Circle size={12} style={{ color: getMapColors().ringBuffer }} /> {t('analysis.ring_buffer')}
            </label>
            {ringEnabled && (
              <TimeRow icon={<span />} label="" values={ringDists} onChange={setRingDists} unit={t('analysis.unit_km')} />
            )}
          </div>
        </div>

        {/* Start button */}
        <button
          onClick={handleStart}
          disabled={!canStart}
          className={cn(
            'w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded text-xs font-medium transition-colors',
            !canStart
              ? 'bg-gn-tertiary text-gn-secondary cursor-not-allowed'
              : 'bg-gn-blue text-gn-inverse hover:bg-gn-blue',
          )}
        >
          {isLoading ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
          {t('analysis.start', { title: t('cannib.title') })}
        </button>

        {/* Validation hints */}
        {!canStart && !isLoading && (
          <div className="text-[9px] text-gn-secondary space-y-0.5">
            {!hasName && <div>• {t('cannib.need_name')}</div>}
            {!hasPoint1 && <div>• {t('cannib.need_point', { num: 1 })}</div>}
            {!hasPoint2 && <div>• {t('cannib.need_point', { num: 2 })}</div>}
          </div>
        )}
      </div>
    </DraggablePanel>
    </>
  );
};
